import { useState } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { ScryfallCard, ScryfallCardWithCount } from "@/types/scryfall";
import { Card as UiCard, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent, BarChart, Bar, XAxis, YAxis, PieChart, Pie, Cell } from "@/components/ui/chart";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { MTGCard } from "./MTGCard";
import { CardDetailsDialog } from "./CardDetailsDialog";

interface ListStatsProps {
  mainboardCards: ScryfallCardWithCount[];
  sideboardCards: ScryfallCardWithCount[];
  format?: string;
  commanders?: ScryfallCard[];
  companion?: ScryfallCard | null;
  activeFilter: { type: string, value: string | number } | null;
  setActiveFilter: (filter: { type: string, value: string | number } | null) => void;
  filteredCards: ScryfallCardWithCount[];
  onCardClick: (card: ScryfallCard) => void;
  cardIsLegal: (card: ScryfallCard, format: string) => boolean;
  getCardTypes: (card: ScryfallCard) => string[];
}

const colorHexMap: Record<string, string> = {
  W: '#FFF9C4',  // Bright, warm white/yellow
  U: '#2196F3',  // Vibrant blue
  B: '#424242',  // Rich dark gray (instead of pure black for visibility)
  R: '#F44336',  // Bright red
  G: '#4CAF50',  // Vibrant green
  C: '#9E9E9E',  // Medium gray for colorless
};

const cardTypeColors = [
  'hsl(var(--primary))',
  '#8884d8',
  '#82ca9d',
  '#ffc658',
  '#ff8042',
  '#00C49F',
  '#FFBB28',
  '#FF8042',
  '#0088FE',
];

const PieChartLegend = ({ data, colors, colorKey = 'name', className }: { data: { name: string; value: number }[], colors: string[] | Record<string, string>, colorKey?: string, className?: string }) => (
  <div className={cn("space-y-1 text-sm", className)}>
    {data.map((entry, index) => {
      const color = Array.isArray(colors)
        ? colors[index % colors.length]
        : colors[entry[colorKey as keyof typeof entry] as string];

      return (
        <div key={entry.name} className="flex items-center gap-2">
          <div
            className="w-2.5 h-2.5 rounded-full"
            style={{ backgroundColor: color }}
          />
          <span className="text-muted-foreground truncate">{entry.name}</span>
          <span className="ml-auto font-semibold">{entry.value}</span>
        </div>
      );
    })}
  </div>
);

const FilteredCardList = ({ cards, onCardClick }: { cards: ScryfallCardWithCount[], onCardClick: (card: ScryfallCard) => void }) => (
    <div className="mt-4 text-sm">
        <ul className="space-y-1">
            {cards.map(card => (
                <li
                    key={`${card.id}-${card.is_foil}`}
                    className="flex justify-between items-center cursor-pointer hover:bg-muted/50 p-1 rounded"
                    onClick={(e) => {
                        e.stopPropagation();
                        onCardClick(card);
                    }}
                >
                    <span>{card.name}</span>
                    <span>{card.count}</span>
                </li>
            ))}
        </ul>
    </div>
);


export const ListStats = ({ mainboardCards, sideboardCards, format, commanders, companion, activeFilter, setActiveFilter, filteredCards, onCardClick, cardIsLegal, getCardTypes }: ListStatsProps) => {
  const [detailsCard, setDetailsCard] = useState<ScryfallCard | null>(null);
  const allCards = [...mainboardCards, ...sideboardCards];

  if (!allCards || allCards.length === 0) {
    return (
      <UiCard onClick={() => setActiveFilter(null)}>
        <CardHeader>
          <CardTitle>Deck Stats</CardTitle>
          <CardDescription>Statistics will appear here once you add cards.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-40 w-full" />
        </CardContent>
      </UiCard>
    )
  }

  const calculateLegalities = (cards: ScryfallCardWithCount[]) => {
    if (!format) return null;
    return cards.reduce((acc, card) => {
      const count = card.count || 1;
      if (cardIsLegal(card, format)) {
        acc.legal += count;
      } else {
        acc.illegal += count;
      }
      return acc;
    }, { legal: 0, illegal: 0 });
  };

  const mainboardLegalities = calculateLegalities(mainboardCards);
  const sideboardLegalities = calculateLegalities(sideboardCards);

  const cardsForCurveAndCmc = mainboardCards.filter(card => {
    const types = getCardTypes(card);
    // Exclude cards that are only lands
    if (types.length === 1 && types[0] === 'Land') {
      return false;
    }
    return true;
  });

  const totalCardsForCmc = cardsForCurveAndCmc.reduce((sum, card) => sum + (card.count || 1), 0);
  const averageCmc = totalCardsForCmc > 0
    ? (cardsForCurveAndCmc.reduce((sum, card) => sum + card.cmc * (card.count || 1), 0) / totalCardsForCmc).toFixed(2)
    : '0.00';

  const cardTypes = allCards.reduce((acc, card) => {
    const types = getCardTypes(card);
    const count = card.count || 1;
    types.forEach(type => {
      acc[type] = (acc[type] || 0) + count;
    });
    return acc;
  }, {} as Record<string, number>);

  const colors = allCards.reduce((acc, card) => {
    const count = card.count || 1;
    if (card.color_identity.length === 0) {
      acc['C'] = (acc['C'] || 0) + count;
    } else {
      card.color_identity.forEach(color => {
        acc[color] = (acc[color] || 0) + count;
      });
    }
    return acc;
  }, {} as Record<string, number>);

  const manaCurve = Array.from({ length: 8 }, (_, i) => ({ cmc: i, count: 0 }));
  cardsForCurveAndCmc.forEach(card => {
    const cmc = Math.min(card.cmc, 7);
    if (manaCurve[cmc]) {
      manaCurve[cmc].count += (card.count || 1);
    }
  });
  const chartData = manaCurve.map(d => ({ ...d, cmc: d.cmc === 7 ? '7+' : d.cmc.toString() }));
  const maxCount = Math.max(...chartData.map(d => d.count));

  const colorData = Object.entries(colors).map(([name, value]) => ({ name, value })).sort((a, b) => 'WUBRGC'.indexOf(a.name) - 'WUBRGC'.indexOf(b.name));
  const cardTypeData = Object.entries(cardTypes).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);

  return (
    <>
      <UiCard onClick={() => setActiveFilter(null)}>
      <CardHeader>
        <CardTitle>Deck Stats</CardTitle>
        <CardDescription>An overview of your deck's characteristics.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {(commanders && commanders.length > 0 || companion) && (
          <div className="mb-6">
            <div className={"grid gap-4 grid-cols-1"}>
              {commanders && commanders.map(commander => (
                <div key={commander.id} className="flex flex-col items-center gap-2 text-center max-w-xs mx-auto">
                  <MTGCard
                    card={commander}
                    imageClassName="w-full rounded-lg"
                    showCount={false}
                    enable3D={true}
                    onClick={() => setDetailsCard(commander)}
                  />
                  <p className="font-semibold text-sm leading-tight">{commander.name}</p>
                  <p className="text-xs text-muted-foreground -mt-1">Commander</p>
                </div>
              ))}
            </div>
            {companion && (
              <div className="flex flex-col items-center gap-2 text-center max-w-xs mx-auto mt-4">
                <MTGCard
                  card={companion}
                  imageClassName="w-full rounded-lg"
                  showCount={false}
                  enable3D={true}
                  onClick={() => setDetailsCard(companion)}
                />
                <p className="font-semibold text-sm leading-tight">{companion.name}</p>
                <p className="text-xs text-muted-foreground -mt-1">Companion</p>
              </div>
            )}
            <Separator className="mt-6" />
          </div>
        )}
        <div className="grid grid-cols-2 gap-4 text-center">
          <div>
            <p className="text-sm text-muted-foreground">Avg. Mana Cost</p>
            <p className="text-3xl font-bold">{averageCmc}</p>
          </div>
          {format && (mainboardLegalities || sideboardLegalities) && (
            <div>
              <p className="text-sm text-muted-foreground">Format Legality</p>
              {mainboardLegalities && (
                <div
                  className="cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveFilter({ type: 'legality', value: 'legal' });
                  }}
                >
                  <p className={`text-lg font-bold ${mainboardLegalities.illegal > 0 ? 'text-destructive' : 'text-green-600'}`}>
                    {mainboardLegalities.legal} <span className="text-xs text-muted-foreground">Main</span>
                  </p>
                  {mainboardLegalities.illegal > 0 &&
                    <p
                      className="text-sm text-destructive"
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveFilter({ type: 'legality', value: 'illegal' });
                      }}
                    >
                      {mainboardLegalities.illegal} Illegal
                    </p>
                  }
                </div>
              )}
              {sideboardLegalities && (
                <div
                  className="cursor-pointer mt-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveFilter({ type: 'legality', value: 'legal' });
                  }}
                >
                  <p className={`text-lg font-bold ${sideboardLegalities.illegal > 0 ? 'text-destructive' : 'text-green-600'}`}>
                    {sideboardLegalities.legal} <span className="text-xs text-muted-foreground">Side</span>
                  </p>
                  {sideboardLegalities.illegal > 0 &&
                    <p
                      className="text-sm text-destructive"
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveFilter({ type: 'legality', value: 'illegal' });
                      }}
                    >
                      {sideboardLegalities.illegal} Illegal
                    </p>
                  }
                </div>
              )}
            </div>
          )}
        </div>
        {activeFilter?.type === 'legality' && <FilteredCardList cards={filteredCards} onCardClick={onCardClick} />}

        <Separator />

        <div onClick={(e) => e.stopPropagation()}>
          <h4 className="font-medium text-sm text-muted-foreground mb-2 text-center">Mana Curve</h4>
          <ChartContainer config={{}} className="h-32 w-full">
            <BarChart
              data={chartData}
              margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
              onClick={(chart) => {
                if (chart && chart.activePayload) {
                  const payload = chart.activePayload[0].payload;
                  const cmcString = payload.cmc.replace('+', '');
                  const cmc = parseInt(cmcString, 10);
                  setActiveFilter({ type: 'cmc', value: cmc });
                }
              }}
            >
              <XAxis dataKey="cmc" tickLine={false} axisLine={false} className="text-xs" />
              <YAxis hide domain={[0, maxCount]} />
              <ChartTooltip
                cursor={false}
                content={<ChartTooltipContent hideLabel className="bg-background/80 backdrop-blur-sm" />}
                formatter={(value) => [`${value} cards`]}
              />
              <Bar dataKey="count" fill="hsl(var(--primary))" radius={2} />
            </BarChart>
          </ChartContainer>
          {activeFilter?.type === 'cmc' && <FilteredCardList cards={filteredCards} onCardClick={onCardClick} />}
        </div>

        <Separator />

        <div onClick={(e) => e.stopPropagation()}>
          <h4 className="font-medium text-sm text-muted-foreground mb-2 text-center">Color Identity</h4>
          <div className="grid grid-cols-2 items-center gap-4">
            <ChartContainer config={{}} className="h-36 w-full">
              <PieChart>
                <ChartTooltip content={<ChartTooltipContent hideLabel />} />
                <Pie
                  data={colorData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={35}
                  outerRadius={60}
                  strokeWidth={1}
                  onClick={(data) => {
                      setActiveFilter({ type: 'color', value: data.name });
                  }}
                >
                  {colorData.map((entry) => (
                    <Cell key={`cell-${entry.name}`} fill={colorHexMap[entry.name]} />
                  ))}
                </Pie>
              </PieChart>
            </ChartContainer>
            <PieChartLegend data={colorData} colors={colorHexMap} />
          </div>
          {activeFilter?.type === 'color' && <FilteredCardList cards={filteredCards} onCardClick={onCardClick} />}
        </div>

        <Separator />

        <div onClick={(e) => e.stopPropagation()}>
          <h4 className="font-medium text-sm text-muted-foreground mb-2 text-center">Card Types</h4>
          <div className="grid grid-cols-2 items-center gap-4">
            <ChartContainer config={{}} className="h-36 w-full">
              <PieChart>
                <ChartTooltip content={<ChartTooltipContent hideLabel />} />
                <Pie
                  data={cardTypeData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={35}
                  outerRadius={60}
                  strokeWidth={1}
                  onClick={(data) => {
                      setActiveFilter({ type: 'type', value: data.name });
                  }}
                >
                  {cardTypeData.map((entry, index) => (
                    <Cell key={`cell-${entry.name}`} fill={cardTypeColors[index % cardTypeColors.length]} />
                  ))}
                </Pie>
              </PieChart>
            </ChartContainer>
            <PieChartLegend data={cardTypeData} colors={cardTypeColors} className="text-xs" />
          </div>
          {activeFilter?.type === 'type' && <FilteredCardList cards={filteredCards} onCardClick={onCardClick} />}
        </div>
      </CardContent>
      </UiCard>
      {detailsCard && (
        <CardDetailsDialog
          card={detailsCard}
          open={!!detailsCard}
          onOpenChange={(open) => !open && setDetailsCard(null)}
        />
      )}
    </>
  );
};
